import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, ShoppingBag, Heart, PawPrint } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { ShareMenu } from "@/components/ShareMenu";
import { Button } from "@/components/ui/button";
import api, { API } from "@/lib/api";
import { customerApi } from "@/context/CustomerContext";
import { productName, productDesc, productImg, AvailabilityBadge, PriceTag } from "@/pages/ShopPage";
import { variantPrice, leadTextFull } from "@/lib/shop";

export default function ProductPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const s = t.shop;
  const navigate = useNavigate();
  const { add } = useCart();
  const { customer } = useCustomer();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [wish, setWish] = useState(false);

  useEffect(() => {
    setP(null);
    setNotFound(false);
    api
      .get(`/shop/products/${id}`)
      .then((r) => {
        setP(r.data);
        const hasChoice = r.data.variants.some((v) => v.label || v.size || v.color || v.custom);
        // Products with options: preselect the FIRST available option so every axis starts filled
        const firstAvail = r.data.variants.find((v) => v.availability !== "unavailable") || r.data.variants[0];
        setVariant(firstAvail || null);
        setImgIdx(0);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    setImgIdx(0);
    if (variant) {
      const m = variant.availability === "in_stock" ? Math.max(1, Number(variant.stock ?? 1)) : 20;
      setQty((q) => Math.min(q, m));
    }
  }, [variant?.id]);

  useEffect(() => {
    if (customer && customer.wishlist) setWish(customer.wishlist.includes(id));
  }, [customer, id]);

  const toggleWish = async () => {
    if (!customer) {
      toast.info(s.wishlist_login);
      navigate("/account");
      return;
    }
    try {
      if (wish) {
        await customerApi.delete(`/account/wishlist/${id}`);
        setWish(false);
        toast.success(s.wishlist_remove);
      } else {
        await customerApi.post("/account/wishlist", { product_id: id });
        setWish(true);
        toast.success(s.wishlist_add);
      }
    } catch {
      toast.error("Error");
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-brand-soft/30">
        <Navbar />
        <main className="pt-40 pb-24 text-center">
          <p className="text-brand-ink/50">{s.empty}</p>
          <Button onClick={() => navigate("/shop")} className="mt-6 rounded-full bg-brand text-brand-cream font-bold" data-testid="product-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> {s.back_shop}
          </Button>
        </main>
        <Footer />
      </div>
    );
  }
  if (!p) return null;

  const hasSizes = (p.sizes || []).length > 0;
  const hasColors = (p.colors || []).length > 0;
  const hasCustom = (p.custom_axis_options || []).length > 0;
  const hasAxes = hasSizes || hasColors || hasCustom;
  const hasVariantLabels = p.variants.some((v) => v.label || v.size || v.color || v.custom);
  const onOrderCallOnly = variant?.availability === "on_order" && p.online_orderable === false;
  const canBuy = variant && variant.availability !== "unavailable" && !onOrderCallOnly;
  const sizeOpts = [...new Set(p.variants.map((v) => v.size).filter(Boolean))];
  const colorOpts = [...new Set(p.variants.map((v) => v.color).filter(Boolean))];
  const customOpts = [...new Set(p.variants.map((v) => v.custom).filter(Boolean))];
  const pick = (patch) => {
    const nv = { size: variant?.size || "", color: variant?.color || "", custom: variant?.custom || "", ...patch };
    const match = p.variants.find((v) => (v.size || "") === nv.size && (v.color || "") === nv.color && (v.custom || "") === nv.custom);
    if (match) setVariant(match);
    else setVariant({ ...nv, id: null, availability: "unavailable", lead_days: null, price: null, sale_price: null, label: "" });
  };
  const selSize = variant?.size || "";
  const selColor = variant?.color || "";
  const selCustom = variant?.custom || "";
  // Max cart quantity = real stock for in-stock variants (no overselling)
  const maxQty = variant?.availability === "in_stock" ? Math.max(1, Number(variant?.stock ?? 1)) : 20;
  // Availability hint per option, relative to the OTHER selected axes (green dot = in stock, amber = on order, grey = none)
  const optStatus = (axis, val) => {
    const cand = p.variants.filter((v) =>
      (v[axis] || "") === val &&
      (axis === "size" || !hasSizes || !selSize || (v.size || "") === selSize) &&
      (axis === "color" || !hasColors || !selColor || (v.color || "") === selColor) &&
      (axis === "custom" || !hasCustom || !selCustom || (v.custom || "") === selCustom)
    );
    if (!cand.length) return "none";
    if (cand.some((v) => v.availability === "in_stock")) return "in_stock";
    if (cand.some((v) => v.availability === "on_order")) return "on_order";
    return "unavailable";
  };
  const StatusDot = ({ st }) => (
    <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${st === "in_stock" ? "bg-bamboo" : st === "on_order" ? "bg-amber-400" : "bg-brand-ink/25"}`} data-testid={`opt-status-${st}`} />
  );

  // Gallery: a selected variant with its own photos shows only those; otherwise all photos
  const allIdx = p.images.map((_, i) => i);
  const variantImgs = variant?.id ? allIdx.filter((i) => (p.image_variants || [])[i] === variant.id) : [];
  const shownIdx = variant?.id && variantImgs.length ? variantImgs : allIdx;

  const addToCart = () => {
    if (!variant && hasVariantLabels) {
      toast.info(s.select_variant);
      return;
    }
    add(p, variant, qty, shownIdx.length ? p.images[shownIdx[0]] : null);
    toast.success(s.added);
  };
  const shareUrl = `${API}/share/product/${p.id}`;

  return (
    <div className="min-h-screen bg-brand-soft/30">
      <Seo
        title={`${productName(p, lang)} | Care For Me Boutique`}
        description={(productDesc(p, lang) || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160) || t.seo.shop_desc}
        path={`/shop/product/${p.id}`}
        image={productImg(p) || undefined}
        type="product"
      />
      <Navbar />
      <main className="pt-28 pb-24 px-5 sm:px-8 max-w-6xl mx-auto" data-testid="product-page">
        <button onClick={() => navigate("/shop")} className="inline-flex items-center gap-2 text-sm font-bold text-brand hover:text-brand-hover transition-colors no-a11y-underline" data-testid="product-back">
          <ArrowLeft className="h-4 w-4" /> {s.back_shop}
        </button>

        <div className="mt-6 grid gap-10 lg:grid-cols-2">
          {/* Gallery — before a variant is picked, ALL photos show; a variant with its own photos shows only those */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
            <div className="aspect-square rounded-[2rem] bg-white border border-brand/10 overflow-hidden shadow-[0_16px_50px_rgba(134,77,99,0.10)]">
              {shownIdx.length ? (
                <img src={productImg(p, shownIdx[Math.min(imgIdx, shownIdx.length - 1)])} alt={productName(p, lang)} className="h-full w-full object-cover" data-testid="product-image" />
              ) : (
                <div className="h-full w-full grid place-items-center text-brand/20">
                  <PawPrint className="h-24 w-24" />
                </div>
              )}
            </div>
            {shownIdx.length > 1 && (
              <div className="mt-4 flex gap-3 flex-wrap">
                {shownIdx.map((gi, pos) => (
                  <button
                    key={gi}
                    onClick={() => setImgIdx(pos)}
                    className={`h-20 w-20 rounded-2xl overflow-hidden border-2 transition-colors ${pos === imgIdx ? "border-brand" : "border-transparent opacity-70 hover:opacity-100"}`}
                    data-testid={`product-thumb-${gi}`}
                  >
                    <img src={`${productImg(p, gi)}`} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Details */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            {p.brand && <p className="text-xs font-bold uppercase tracking-widest text-brand-ink/40">{p.brand}</p>}
            <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-bold text-brand-ink" data-testid="product-name">{productName(p, lang)}</h1>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <PriceTag p={p} variant={variant} big />
              <AvailabilityBadge availability={variant?.availability || p.availability} lead={variant?.lead_days ?? p.lead_days} />
            </div>
            <p className="mt-1 text-xs text-brand-ink/50">{s.vat_note}</p>

            {hasVariantLabels && !hasAxes && (
              <div className="mt-6">
                <p className="text-sm font-bold text-brand-ink mb-2">{s.select_variant}</p>
                <div className="flex flex-wrap gap-2" data-testid="variant-selector">
                  {p.variants.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setVariant(v)}
                      disabled={v.availability === "unavailable"}
                      className={`rounded-full px-4 py-2 text-sm font-bold border transition-colors no-a11y-underline ${
                        variant?.id === v.id
                          ? "bg-brand text-brand-cream border-brand"
                          : v.availability === "unavailable"
                            ? "bg-stone-alt/40 text-brand-ink/30 border-brand/10 line-through cursor-not-allowed"
                            : "bg-white text-brand-ink border-brand/20 hover:border-brand"
                      }`}
                      data-testid={`variant-${v.id}`}
                    >
                      {v.label || s.details}
                      <span className={variant?.id === v.id ? "opacity-80" : "text-brand/70"}>{v.label ? " · " : ""}€{variantPrice(p, v).toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {hasAxes && (
              <div className="mt-6 space-y-4" data-testid="variant-selector">
                {hasSizes && sizeOpts.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-brand-ink mb-2">{s.select_size || "Μέγεθος"}</p>
                    <div className="flex flex-wrap gap-2">
                      {sizeOpts.map((sz) => {
                        const active = selSize === sz;
                        const st = optStatus("size", sz);
                        const anyAvail = st !== "unavailable" && st !== "none";
                        return (
                          <button key={sz} onClick={() => pick({ size: sz })} disabled={!anyAvail} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold border transition-colors no-a11y-underline ${active ? "bg-brand text-brand-cream border-brand" : !anyAvail ? "bg-stone-alt/40 text-brand-ink/30 border-brand/10 line-through cursor-not-allowed" : "bg-white text-brand-ink border-brand/20 hover:border-brand"}`} data-testid={`size-${sz}`}>
                            <StatusDot st={st} />{sz}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {hasColors && colorOpts.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-brand-ink mb-2">{s.select_color || "Χρώμα"}</p>
                    <div className="flex flex-wrap gap-2">
                      {colorOpts.map((cid) => {
                        const cv = p.variants.find((v) => v.color === cid);
                        const name = lang === "el" ? (cv?.color_name || "") : (cv?.color_name_en || cv?.color_name || "");
                        const active = selColor === cid;
                        const st = optStatus("color", cid);
                        const anyAvail = st !== "unavailable" && st !== "none";
                        return (
                          <button key={cid} onClick={() => pick({ color: cid })} disabled={!anyAvail} className={`inline-flex items-center gap-2 rounded-full border pl-1.5 pr-3.5 py-1.5 text-sm font-bold transition-colors no-a11y-underline ${active ? "bg-brand text-brand-cream border-brand" : !anyAvail ? "bg-stone-alt/40 text-brand-ink/30 border-brand/10 line-through cursor-not-allowed" : "bg-white text-brand-ink border-brand/20 hover:border-brand"}`} data-testid={`color-${cid}`}>
                            {cv?.color_image ? (
                              <img src={`${API}/${cv.color_image}`} alt="" className="h-7 w-7 rounded-full object-cover border border-white/60" />
                            ) : (
                              <span className="h-7 w-7 rounded-full bg-stone-alt/70 grid place-items-center"><PawPrint className="h-3.5 w-3.5 text-brand/30" /></span>
                            )}
                            {name}
                            <StatusDot st={st} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {hasCustom && customOpts.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-brand-ink mb-2">{(lang === "el" ? p.custom_axis_name_gr : p.custom_axis_name_en) || p.custom_axis_name_gr || s.details}</p>
                    <div className="flex flex-wrap gap-2">
                      {customOpts.map((cu) => {
                        const active = selCustom === cu;
                        const st = optStatus("custom", cu);
                        const anyAvail = st !== "unavailable" && st !== "none";
                        return (
                          <button key={cu} onClick={() => pick({ custom: cu })} disabled={!anyAvail} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold border transition-colors no-a11y-underline ${active ? "bg-brand text-brand-cream border-brand" : !anyAvail ? "bg-stone-alt/40 text-brand-ink/30 border-brand/10 line-through cursor-not-allowed" : "bg-white text-brand-ink border-brand/20 hover:border-brand"}`} data-testid={`custom-${cu}`}>
                            <StatusDot st={st} />{cu}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {variant && !variant.id && (
                  <p className="text-sm font-bold text-rose-600" data-testid="combo-unavailable">{s.unavailable || "Μη διαθέσιμο"}</p>
                )}
                <p className="text-[11px] text-brand-ink/45" data-testid="variant-legend">{s.legend_hint}</p>
                {variant?.id && variant.availability === "in_stock" && variant.stock > 0 && variant.stock <= 5 && (
                  <p className="text-xs font-bold text-bamboo-deep" data-testid="stock-left">{s.stock_left} {variant.stock}</p>
                )}
                {variant?.id && (variant.price != null || variant.sale_price != null) && (
                  <p className="text-lg font-bold text-brand" data-testid="variant-price-line">
                    €{variantPrice(p, variant).toFixed(2)}
                    {variant.sale_price != null && variant.price != null && <span className="ml-2 text-sm line-through text-brand-ink/40">€{Number(variant.price).toFixed(2)}</span>}
                  </p>
                )}
              </div>
            )}

            {onOrderCallOnly ? (
              <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4" data-testid="call-to-order-note">
                <p className="text-sm font-bold text-amber-800">{s.call_to_order}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <a href="tel:2111102354" className="inline-flex items-center gap-1.5 rounded-full bg-brand text-brand-cream text-sm font-bold px-4 py-2 no-a11y-underline" data-testid="call-to-order-tel">
                    211 110 2354
                  </a>
                  <a href="tel:6947527558" className="inline-flex items-center gap-1.5 rounded-full border border-brand/30 text-brand text-sm font-bold px-4 py-2 no-a11y-underline" data-testid="call-to-order-tel2">
                    694 752 7558
                  </a>
                </div>
              </div>
            ) : (
              <div className="mt-8 flex items-center gap-3 flex-wrap">
                <div className="flex items-center rounded-full border border-brand/20 bg-white">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-11 w-11 grid place-items-center text-brand-ink" data-testid="qty-minus" aria-label="minus">
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-8 text-center font-bold text-brand-ink" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => Math.min(maxQty, q + 1))} disabled={qty >= maxQty} className="h-11 w-11 grid place-items-center text-brand-ink disabled:opacity-30" data-testid="qty-plus" aria-label="plus">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <Button
                  onClick={addToCart}
                  disabled={!variant || !canBuy}
                  className="flex-1 min-w-44 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 text-base"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingBag className="h-5 w-5 mr-2" /> {s.add_to_cart}
                </Button>
                <Button
                  onClick={toggleWish}
                  variant="outline"
                  size="icon"
                  className={`rounded-full h-12 w-12 border-brand/20 ${wish ? "text-rose-500 border-rose-300 bg-rose-50" : "text-brand-ink/50"}`}
                  data-testid="wishlist-toggle"
                  aria-label="wishlist"
                >
                  <Heart className={`h-5 w-5 ${wish ? "fill-rose-500" : ""}`} />
                </Button>
                <ShareMenu
                  url={shareUrl}
                  title={productName(p, lang)}
                  iconOnly
                  testid="product-share"
                  triggerClassName="grid place-items-center rounded-full h-12 w-12 border border-brand/20 bg-white text-brand-ink/50 hover:text-brand hover:border-brand transition-colors"
                />
              </div>
            )}
            {variant?.availability === "on_order" && (
              <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-2.5" data-testid="on-order-note">
                {s.avail_on_order}{(variant.lead_days ?? p.lead_days) ? ` · ${leadTextFull(lang, variant.lead_days ?? p.lead_days)}` : ""}
              </p>
            )}
            <p className="mt-6 text-sm text-brand-ink/60 bg-bamboo-soft/60 border border-bamboo/20 rounded-2xl px-4 py-3">{s.pickup_note}</p>
          </motion.div>
        </div>

        {productDesc(p, lang) && productDesc(p, lang) !== "<p></p>" && (
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-12 bg-white rounded-[2rem] border border-brand/10 p-7 sm:p-10 shadow-[0_8px_30px_rgba(134,77,99,0.06)]"
            data-testid="product-description"
          >
            <h2 className="font-heading text-3xl font-bold text-brand-ink mb-5">{s.details}</h2>
            <div className="rich-desc" dangerouslySetInnerHTML={{ __html: productDesc(p, lang) }} />
          </motion.section>
        )}
      </main>

      {/* Sticky mobile buy bar — price + add-to-cart always visible on phones */}
      {!onOrderCallOnly && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-brand/10 pl-14 pr-4 py-3 flex items-center gap-3 shadow-[0_-8px_24px_rgba(134,77,99,0.12)]" data-testid="sticky-buy-bar">
          <PriceTag p={p} variant={variant} />
          <Button
            onClick={addToCart}
            disabled={!variant || !canBuy}
            className="flex-1 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11"
            data-testid="sticky-add-to-cart"
          >
            <ShoppingBag className="h-4 w-4 mr-2" /> {s.add_to_cart}
          </Button>
        </div>
      )}
      <Footer />
    </div>
  );
}
